import { useEffect, useRef, useState } from "react";

interface UseInViewOptions {
  once?: boolean;
  margin?: string;
  threshold?: number;
}

/**
 * SSR-safe alternative to framer-motion's useInView hook
 * Uses Intersection Observer API with proper client-side only initialization
 */
export function useInViewSSR<T extends HTMLElement = HTMLDivElement>(
  options: UseInViewOptions = {}
) {
  const { once = false, margin = "0px", threshold = 0 } = options;
  const [isInView, setIsInView] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const elementRef = useRef<T>(null);

  useEffect(() => {
    // Mark that we're now on the client side
    setIsClient(true);
  }, []);

  useEffect(() => {
    // Only initialize Intersection Observer on the client side
    if (!isClient || typeof window === "undefined") return;
    
    const element = elementRef.current;
    if (!element) return;

    // Check if Intersection Observer is supported
    if (typeof IntersectionObserver === "undefined") {
      // Fallback: assume element is in view
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isElementInView = entry.isIntersecting;
        setIsInView(isElementInView);

        // If 'once' is true, disconnect observer after first intersection
        if (isElementInView && once) {
          observer.disconnect();
        }
      },
      {
        rootMargin: margin,
        threshold,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [isClient, margin, threshold, once]);

  // During SSR or before client-side hydration, return default values
  return { ref: elementRef, isInView: isClient ? isInView : false };
}